import { useEffect, useRef } from 'react';

type KeyRecorderOptions = { windowSize: number };
type ModifierKeys = keyof Pick<KeyboardEvent, 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey'>;
type RecordEvent = {
    code: string;
    event: KeyboardEvent;
    key: string;
    modifiers: string[];
    time: string;
};
const modifierKeys: ModifierKeys[] = ['ctrlKey', 'altKey', 'metaKey', 'shiftKey'];

const getModifiers = (event: KeyboardEvent) => {
    return modifierKeys.reduce((result: string[], key: ModifierKeys) => {
        if (event[key]) {
            result.push(key);
        }
        return result;
    }, []);
};

export const useKeyRecorder = (options?: KeyRecorderOptions) => {
    const keyRecorderRef = useRef<KeyRecorder>();

    useEffect(() => {
        if (keyRecorderRef.current === undefined) {
            keyRecorderRef.current = new KeyRecorder(options);
        }
        return () => {
            keyRecorderRef.current?.kill();
        };
    }, [options]);

    return { keyRecorder: keyRecorderRef.current };
};

class KeyRecorder {
    private running: boolean;
    private eventListeners: {
        type: string;
        listener: EventListener;
    }[];
    private windowSize: number;

    lastEvent: RecordEvent | null;

    recordedEvents: RecordEvent[];

    constructor({ windowSize }: KeyRecorderOptions = { windowSize: 100 }) {
        this.windowSize = windowSize;
        this.running = false;
        this.eventListeners = [];
        this.recordedEvents = [];
        this.lastEvent = null;

        this.start();
    }

    private recordEvent = (event: Event) => {
        if (event instanceof KeyboardEvent) {
            const recordEvent: RecordEvent = {
                code: event.code,
                event,
                key: event.key,
                modifiers: getModifiers(event),
                time: new Date().toISOString(),
            };
            this.recordedEvents.push(recordEvent);
            this.lastEvent = recordEvent;
            if (this.recordedEvents.length > this.windowSize) {
                this.recordedEvents.shift();
            }
        }
    };

    private addEventListener = (type: string, listener: EventListener) => {
        this.eventListeners.push({
            type,
            listener,
        });

        window.addEventListener(type, listener);
    };

    start() {
        if (!this.running) {
            this.addEventListener('keydown', this.recordEvent);
            this.running = true;
        }
    }

    stop() {
        if (this.running) {
            this.eventListeners.forEach(({ type, listener }) => {
                window.removeEventListener(type, listener);
            });
            this.eventListeners = [];

            this.running = false;
        }
    }

    kill() {
        this.stop();
        this.recordedEvents = [];
    }
}
